/**
 * HOOK: useFormSubmissionMutations
 * V5 R12 — édition inline des champs formulaire depuis la fiche contact.
 *
 * Architecture (Option A) : `form_submissions.data` est un JSONB
 * `{ [field_id]: value }`. On patch un champ via merge JSONB côté client puis
 * UPDATE complet de la colonne `data`.
 *
 * RLS : la policy `form_submissions_superadmin_admin_all` (00004) couvre
 * superadmin + admin. Le rôle `user` n'a que SELECT → l'UPDATE échouera
 * silencieusement (Supabase renvoie data:[] sans error). Le caller doit gérer
 * un toast "permissions" si besoin (cf. usePermissions().canEditProspects).
 */

import { useCallback, useState } from 'react';
import { useSupabase } from './useSupabase';

interface UpdateFieldArgs {
  submissionId: string;
  fieldId: string;
  newValue: unknown;
}

export function useFormSubmissionMutations() {
  const supabase = useSupabase();
  const [saving, setSaving] = useState(false);

  /**
   * Met à jour un champ unique d'une soumission.
   * Pattern : SELECT data → merge {fieldId: newValue} → UPDATE data.
   * On ne peut pas utiliser jsonb_set côté Supabase JS sans RPC, donc on
   * lit-modifie-écrit. Race condition acceptable : l'UI ouvre l'édition
   * sur un seul champ par submission à la fois.
   */
  const updateFormSubmission = useCallback(
    async ({ submissionId, fieldId, newValue }: UpdateFieldArgs): Promise<void> => {
      setSaving(true);
      try {
        // 1. Charger le data actuel (évite d'écraser d'autres champs)
        const { data: current, error: readError } = await supabase
          .from('form_submissions')
          .select('data')
          .eq('id', submissionId)
          .single();

        if (readError) throw readError;

        const merged = { ...(current?.data ?? {}), [fieldId]: newValue };

        // 2. UPDATE complet de la colonne data
        const { error: writeError } = await supabase
          .from('form_submissions')
          .update({ data: merged })
          .eq('id', submissionId);

        if (writeError) throw writeError;
      } finally {
        setSaving(false);
      }
    },
    [supabase]
  );

  /**
   * Met à jour plusieurs champs d'une soumission en une seule écriture.
   * Plus efficient quand l'UI permet d'éditer plusieurs champs avant "Sauvegarder".
   */
  const updateFormSubmissionFields = useCallback(
    async (submissionId: string, patch: Record<string, unknown>): Promise<void> => {
      setSaving(true);
      try {
        const { data: current, error: readError } = await supabase
          .from('form_submissions')
          .select('data')
          .eq('id', submissionId)
          .single();

        if (readError) throw readError;

        const merged = { ...(current?.data ?? {}), ...patch };

        const { error: writeError } = await supabase
          .from('form_submissions')
          .update({ data: merged })
          .eq('id', submissionId);

        if (writeError) throw writeError;
      } finally {
        setSaving(false);
      }
    },
    [supabase]
  );

  return { updateFormSubmission, updateFormSubmissionFields, saving };
}
